import {
  GoodsReceiptsScreen,
  grnRoute,
  PurchaseOrdersScreen,
  purchaseOrderRoute,
  ReportsScreen,
  RequisitionsScreen,
  RfqScreen,
  reportsRoute,
  requisitionRoute,
  rfqRoute,
  SuppliersScreen,
} from '@felix/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { Shell } from '../components/Shell.js';
import { useDashboard } from '../dashboard-data.js';
import {
  officeGoodsReceiptSource,
  officePurchaseOrderEditor,
  officePurchaseOrderSource,
  officePurchasingSource,
  officeReportSource,
  officeRfqSource,
  officeSupplierSource,
} from '../purchasing-source.js';
import { useSession } from '../session.js';
import { officeSupplierAccountSource } from '../supplier-account-source.js';

/** Malzeme talebi (ortak ekran; ADR 0026 §3): ofiste liste, kart ve ofisteki onay adımı. */
export function RequisitionsPage() {
  const { t } = useTranslation();
  const { search } = useLocation();
  return (
    <Shell title={t('purchasing.list.title')}>
      <RequisitionsScreen source={officePurchasingSource} route={requisitionRoute(search)} />
    </Shell>
  );
}

/** Tedarikçiler (ADR 0026 §1; `gemi-satin-alma.jpeg` "Supplier Management"): yalnız ofiste. */
export function SuppliersPage() {
  const { t } = useTranslation();
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const { refresh } = useDashboard();
  // Zil bağlantısı `?profile=<tedarikçi>`: kartın şirket profili ve banka hesapları (ADR 0114)
  const params = new URLSearchParams(search);
  const profileId = params.get('profile');
  // Zil bağlantısı `?account=<tedarikçi>&tab=invoices|payments`: kartın cari penceresi (ADR 0119)
  const accountId = params.get('account');
  const accountTab = params.get('tab') === 'payments' ? 'payments' : 'invoices';
  // Kartta cari, ekstre ve geçmiş (ADR 0119); dekont yüklemesi kişiye bağlı
  const owner = useSession().me?.user?.id;
  const source = useMemo(
    () => ({ ...officeSupplierSource, account: officeSupplierAccountSource(owner) }),
    [owner],
  );
  return (
    <Shell title={t('purchasing.suppliers.title')}>
      <SuppliersScreen
        source={source}
        initialProfileId={profileId}
        initialAccount={accountId ? { supplierId: accountId, tab: accountTab } : null}
        onProfileClosed={() => {
          if (profileId || accountId) navigate(pathname, { replace: true });
        }}
        onProfileChanged={() => void refresh()}
      />
    </Shell>
  );
}

/** RFQ ve Supplier Comparison (ADR 0026 §1; `satin-alma-akisi.jpeg`): yalnız ofiste. */
export function RfqPage() {
  const { t } = useTranslation();
  const { search } = useLocation();
  return (
    <Shell title={t('purchasing.rfq.title')}>
      <RfqScreen
        source={officeRfqSource}
        route={rfqRoute(search)}
        orders={officePurchaseOrderEditor}
      />
    </Shell>
  );
}

/** Satın alma siparişi (ADR 0040): ofiste yazılır, onaylanır ve tedarikçiye gönderilir. */
export function PurchaseOrdersPage() {
  const { t } = useTranslation();
  const { search } = useLocation();
  return (
    <Shell title={t('purchasing.po.list.title')}>
      <PurchaseOrdersScreen source={officePurchaseOrderSource} route={purchaseOrderRoute(search)} />
    </Shell>
  );
}

/** Mal kabulü (ADR 0040): gemide yazılır; ofiste salt okunur. */
export function GoodsReceiptsPage() {
  const { t } = useTranslation();
  const { search } = useLocation();
  return (
    <Shell title={t('purchasing.grn.list.title')}>
      <GoodsReceiptsScreen source={officeGoodsReceiptSource} route={grnRoute(search)} />
    </Shell>
  );
}

/** Raporlar (ADR 0040 #189; `satin-alma-akisi.jpeg` "Reports"). */
export function ReportsPage() {
  const { t } = useTranslation();
  const { search } = useLocation();
  return (
    <Shell title={t('reports.title')}>
      <ReportsScreen source={officeReportSource} route={reportsRoute(search)} pmsReports />
    </Shell>
  );
}
